"use client";

import { getSelectedMpidRole } from "@/app/actions/getSelectedMpidRole";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useActionQueueContext } from "@/providers/action-queue-provider";
import { Bug, LoaderCircle } from "lucide-react";
import Link from "next/link";

import { useCallback, useState, useTransition } from "react";

export function FeedbackModal() {
  const logActivity = useActionQueueContext();
  const [open, setOpen] = useState(false);
  const [isFetchingRoleMpid, startFetchingRoleMpid] = useTransition();
  const [fetchedRoleMpid, setFetchedRoleMpid] = useState<{
    group_mpid: string;
    role: string;
  } | null>(null);

  const handleOnFetchRoleMpid = useCallback(() => {
    logActivity({
      action: `site_navigation_menu.issues_feedback.clicked`,
      info: "User tried to open issues/feedback modal",
      unstructured_json: {},
    });
    startFetchingRoleMpid(async () => {
      try {
        const selectedRoleMpid = await getSelectedMpidRole();

        setFetchedRoleMpid(selectedRoleMpid);

        if (selectedRoleMpid) {
          setOpen(true);
        }
      } catch (ex) {
        console.log(ex);
        setFetchedRoleMpid(null);
      }
    });
  }, [logActivity]);

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (isFetchingRoleMpid) return;

        if (fetchedRoleMpid) {
          setOpen(false);
        } else if (!value) {
          setFetchedRoleMpid(null);
          setOpen(false);
        } else {
          setOpen(true);
        }
      }}
    >
      <DialogTrigger asChild>
        <Button
          variant="destructive"
          onClick={handleOnFetchRoleMpid}
          disabled={isFetchingRoleMpid}
        >
          {isFetchingRoleMpid ? (
            <LoaderCircle className="animate-spin" />
          ) : (
            <Bug size={16} />
          )}
          Issues/Feedback
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Report an issue or give feedback</DialogTitle>
        </DialogHeader>
        <div>
          <span>
            {
              "If you would like to report any observations or issues please email "
            }
          </span>
          <Link
            href="mailto:Kestrel@mhhsprogramme.co.uk"
            target="_blank"
            className="font-bold underline"
          >
            Kestrel@mhhsprogramme.co.uk
          </Link>
          <span>
            {" and provide following information in the subject line:"}
          </span>
          <br />
          <pre className="bg-gray-100">
            {"<Supplier MPID> - <Role> - <Request Type>"}
          </pre>
          <br />
          <span className="font-bold">Example:</span>
          <pre className="bg-gray-100">{`${fetchedRoleMpid?.group_mpid} - ${fetchedRoleMpid?.role} - Validation issue`}</pre>
        </div>
      </DialogContent>
    </Dialog>
  );
}
